import React, { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Paper, Box, Typography } from '@mui/material';

// Utils
import { Storage } from '../../../utils/Storage';

// Services
import { admitUsers, completeRegistration, declineUsers, getAdmissionRequests } from '../../../services/admissionRequest.service';

// Hooks
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';

// Constants
import { SearchBarFilterTypes } from '../../../constants/Constants';

// Common Components
import SearchBar from '../../common/SearchBar/SearchBar';
import CustomModal from '../../common/withModal';
import { useStandardTable as useTable, Table } from '../../common/table/index';

import makeAdmissionRequestColumn from './makeAdmissionRequestColumn';
import AppUserConfig from './components/AppUserConfig';
import StatusFilterButtons from './components/StatusFilterButtons';
import { parseError } from '../../../utils';

type StatusFilter = {
  status: number | null;
  filterType: string;
  or?: StatusFilter;
};
const Admission = () => {
  const { notifyMessage } = useNotifyMessage();
  const [changeRolePermissions, setChangeRolePermissions] = useState<string | undefined>(undefined);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [activeStatusFilters, setActiveStatusFilters] = useState<string[]>(['pending', 'approved']);
  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [
      {
        createdDateTime: '1',
      },
    ],
    filters: [],
  });

  const handlePermissionsAndRoles = (id: string): void => {
    setChangeRolePermissions(id);
  };

  useEffect(() => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.ADMISSIONS);
    if (filterState) {
      setParamFilter({
        ...paramFilter,
        filters: filterState.filters,
      });
    }
  }, []);

  useEffect(() => {
    if (changeRolePermissions) {
      setSelectedUserId(changeRolePermissions);
    }
  }, [changeRolePermissions]);

  useEffect(() => {
    applyStatusFilters();
  }, [activeStatusFilters]);

  const applyStatusFilters = () => {
    // Keeping non-status filters intact
    const nonStatusFilters = paramFilter.filters.filter((f) => f.status === undefined && f.and === undefined && f.or === undefined);

    const statusFilters: StatusFilter[] = [];

    // Add filters based on active toggles
    if (activeStatusFilters.includes('pending')) {
      statusFilters.push({ status: 999, filterType: 'Equal' });
    }
    if (activeStatusFilters.includes('approved')) {
      statusFilters.push({ status: 1, filterType: 'Equal' });
    }
    if (activeStatusFilters.includes('denied')) {
      statusFilters.push({ status: 0, filterType: 'Equal' });
    }
    if (activeStatusFilters.includes('complete')) {
      statusFilters.push({ status: 2, filterType: 'Equal' });
    }

    if (statusFilters.length === 0) {
      statusFilters.push({ status: null, filterType: 'Equal' });
    }

    const combinedStatusFilter = statusFilters.reduce<StatusFilter | undefined>((acc, curr, index) => {
      if (index === 0) return curr;
      return { ...curr, or: acc };
    }, undefined);

    setParamFilter({
      ...paramFilter,
      filters: [...nonStatusFilters, ...(combinedStatusFilter ? [combinedStatusFilter] : [])],
    });
  };

  const handleAdmitUser = async (id: string) => {
    try {
      await admitUsers([id]);
      setParamFilter({ ...paramFilter, changedAt: new Date() });
      notifyMessage('User has been approved', 'success');

      // Reapply current filters to refresh data
      applyStatusFilters();
    } catch (error) {
      notifyMessage('An error occurred during admission', 'error');
    }
  };

  const handleDeclineUser = async (id: string) => {
    try {
      await declineUsers([id]);
      setParamFilter({ ...paramFilter, changedAt: new Date() });
      notifyMessage('User has been declined', 'success');
      applyStatusFilters();
    } catch (error) {
      notifyMessage('An error occurred', 'error');
    }
  };

  const completeUserConfiguration = async (id: string) => {
    try {
      await completeRegistration(id);
      notifyMessage('User admission completed', 'success');
      setParamFilter({ ...paramFilter, changedAt: new Date() });

      // close model on completing config
      setChangeRolePermissions(undefined);
    } catch (err) {
      notifyMessage(parseError(err, 'Error during completion of user admission'), 'error');
    }
  };

  const columns = useMemo(() => makeAdmissionRequestColumn(handleAdmitUser, handleDeclineUser, handlePermissionsAndRoles), []);

  const table = useTable<any>({
    columns,
    accessKey: 'email',
    fetchAsync: getAdmissionRequests,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any) => {
    setParamFilter({
      ...paramFilter,
      filters,
    });
  };

  const handleStatusFilterChange = (statuses: string[]) => {
    setActiveStatusFilters(statuses);
  };

  const handleReset = () => {
    setActiveStatusFilters(['pending', 'approved']);
  };

  return (
    <>
      <Box sx={{ px: { xs: 0.5, sm: 0.75 }, mb: 1.5 }}>
        <Typography variant='h5' sx={{ m: 0, fontWeight: 700, color: '#1f2d63' }}>
          Admission
        </Typography>
        <Typography variant='body2' sx={{ mt: 0.5, color: 'text.secondary' }}>
          Manage user admission requests to the system
        </Typography>
      </Box>

      <React.Fragment>
        <SearchBar
          pageKey={SearchBarFilterTypes.ADMISSIONS}
          onSearch={onSearchChange}
          onReset={handleReset}
          columns={columns}
          allowSearchAll={true}
          components={<StatusFilterButtons activeStatuses={activeStatusFilters} onChange={handleStatusFilterChange} />}
        />
        <Paper>
          <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={false} getCoreRowModel={getCoreRowModel()} />
        </Paper>
      </React.Fragment>
      <CustomModal isOpen={!!changeRolePermissions} setIsOpen={() => setChangeRolePermissions(undefined)} showSaveButton={false} maxWidth='xl'>
        {selectedUserId && <AppUserConfig id={selectedUserId} handleUserConfiguration={completeUserConfiguration} />}
      </CustomModal>
    </>
  );
};

export default Admission;
